import { styled } from '@linaria/react'
import React, { FC, ReactNode } from 'react'
import { VotedStatus } from './VotedStatus'
import { Tag } from './Tag'
import { colors, ColorValue } from '../styles/colors'
import { StatusLabel } from './StatusLabel'
import { FormattedMessage } from 'react-intl'
import StarIcon from '../svgr/Star'
import CheckIcon from '../svgr/Check'
import { css } from '@linaria/core'
import { isTelegramVersionSupported } from '../hooks/useTelegram'

export type TagType =
  | 'collect-reward'
  | 'reward-collected'
  | 'no-reward'
  | 'ended'

export const BoxStatusBar: FC<{
  hasYourVote?: boolean
  className?: string
  expiration: ReactNode
  tags: Record<TagType, boolean>
  onRewardClaim?: () => void

  status?: {
    label: string
    color: ColorValue
    textColor?: ColorValue
  }
}> = ({ className, hasYourVote, status, tags, expiration, onRewardClaim }) => {
  const handleTagClick = () => {
    if (tags['collect-reward'] && onRewardClaim) {
      onRewardClaim()
    } else if (tags['reward-collected']) {
      if (isTelegramVersionSupported(6.1)) {
        window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium')
      }
    }
  }

  const tagElement: Record<TagType, ReactNode> = {
    'collect-reward': (
      <CollectRewardTag onClick={handleTagClick} key="collect-reward-tag" />
    ),
    'reward-collected': (
      <RewardCollectedTag onClick={handleTagClick} key="collect-reward-tag" />
    ),
    'no-reward': <NoRewardTag key="no-reward-tag" />,
    ended: <EndedTag key="ended-tag" />,
  }

  const isShowTags = Object.values(tags).some(Boolean)

  return (
    <Container className={className}>
      {Object.entries(tags).map(([tagName, isShown]) => {
        if (
          isShown &&
          (tagName === 'collect-reward' ||
            tagName === 'reward-collected' ||
            tagName === 'no-reward' ||
            tagName === 'ended')
        ) {
          return tagElement[tagName]
        }
        return null
      })}

      {!isShowTags && (
        <ExpirationContainer className={leftSide}>
          {expiration}
        </ExpirationContainer>
      )}

      {status && (
        <StatusLabel
          label={status.label}
          dotColor={status.color}
          textColor={status.textColor}
          className={leftSide}
        />
      )}

      {hasYourVote && !isShowTags && <VotedStatus className={rightSide} />}
    </Container>
  )
}

const Container = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  z-index: 10;
`

const ExpirationContainer = styled.div`
  display: flex;
`

const leftSide = css`
  position: absolute;
  left: 10px;
  bottom: 10px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`
const rightSide = css`
  position: absolute;
  right: 10px;
  bottom: 10px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`

const CollectRewardTag: FC<{ onClick?: () => void }> = ({ onClick }) => (
  <Tag
    label={
      <FormattedMessage
        id="box.status.takeWinnings.tag"
        defaultMessage="Заберите награду"
      />
    }
    zIndex={10}
    variant="success"
    icon={<StarIcon />}
    className={rightSide}
    isActive
    onClick={onClick}
    isNotInteractive={!onClick}
    typographyProps={{
      fontSize: 13,
      fontWeight: 700,
      color: colors.white,
    }}
  />
)

const RewardCollectedTag: FC<{ onClick?: () => void }> = ({ onClick }) => (
  <Tag
    label={
      <FormattedMessage
        id="box.status.rewardComplete"
        defaultMessage="Вознаграждение получено"
      />
    }
    variant="success"
    icon={<CheckIcon />}
    className={rightSide}
    isActive
    onClick={onClick}
    isNotInteractive={!onClick}
    typographyProps={{
      fontSize: 13,
      fontWeight: 700,
      color: colors.white,
    }}
  />
)

const NoRewardTag = () => (
  <Tag
    label={
      <FormattedMessage id="box.status.noWin" defaultMessage="Без вознаграждения" />
    }
    variant="primary"
    className={rightSide}
    isActive
    isNotInteractive
    typographyProps={{
      fontSize: 13,
      fontWeight: 700,
      color: colors.white,
    }}
  />
)

const EndedTag = () => (
  <Tag
    label={<FormattedMessage id="ended" defaultMessage="Завершен" />}
    variant="transparent"
    className={leftSide}
    isActive
    isNotInteractive
    typographyProps={{
      fontSize: 13,
      fontWeight: 700,
      color: colors.white,
    }}
  />
)
